import React, { useState, useEffect, useCallback } from 'react';
import { Search, X } from 'lucide-react';
import { getProducts, getCategories, getCollections } from '../api';
import ProductCard from '../components/ProductCard';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CartSidebar from '../components/CartSidebar';
import LoginModal from '../components/LoginModal';
import BannerCarousel from '../components/BannerCarousel';
import { Loader2 } from 'lucide-react';
import { Input } from '../components/ui/input';

const HomePage = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState(['Все']);
  const [collections, setCollections] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('Все');
  const [selectedCollection, setSelectedCollection] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadProducts();
  }, [selectedCategory, selectedCollection, searchQuery]);

  const loadData = async () => {
    try {
      const [productsData, categoriesData, collectionsData] = await Promise.all([
        getProducts(),
        getCategories(),
        getCollections()
      ]);
      setProducts(productsData);
      setCategories(categoriesData);
      setCollections(collectionsData);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadProducts = async () => {
    try {
      const data = await getProducts(selectedCategory, selectedCollection, searchQuery);
      setProducts(data);
    } catch (error) {
      console.error('Failed to load products:', error);
    }
  };

  const clearFilters = () => {
    setSelectedCategory('Все');
    setSelectedCollection('');
    setSearchQuery('');
    setShowSearch(false);
  };

  return (
    <div className="min-h-screen bg-brand-surface dark:bg-[#1E1E1E] transition-colors">
      <Header />
      <CartSidebar />
      <LoginModal />

      <main className="pt-20 md:pt-24 pb-20">
        <div className="max-w-[1920px] mx-auto px-4 md:px-8 lg:px-12">
          {/* Banner Carousel */}
          <BannerCarousel />

          {/* Filters Row */}
          <div className="mb-8 space-y-4">
            {/* Search and Categories */}
            <div className="flex items-center gap-3">
              {/* Search Toggle */}
              <button
                onClick={() => setShowSearch(!showSearch)}
                className={`p-3 rounded-xl transition-all ${
                  showSearch || searchQuery
                    ? 'bg-brand-accent dark:bg-brand-accent text-brand-text dark:text-brand-text'
                    : 'bg-brand-surface dark:bg-brand-surface text-brand-text dark:text-brand-text hover:bg-brand-surface dark:hover:bg-brand-surface'
                }`}
                title="Поиск"
              >
                <Search className="w-5 h-5" />
              </button>

              {/* Categories */}
              <div className="flex-1 overflow-x-auto pb-2 scrollbar-hide">
                <div className="flex gap-2 md:gap-3 min-w-max">
                  {categories.map(cat => (
                    <button
                      key={cat}
                      onClick={() => setSelectedCategory(cat)}
                      className={`px-4 md:px-6 py-2 md:py-3 text-sm md:text-base font-medium transition-all rounded-xl ${
                        selectedCategory === cat
                          ? 'bg-brand-accent dark:bg-brand-accent text-brand-text dark:text-brand-text'
                          : 'bg-brand-surface dark:bg-brand-surface text-brand-text dark:text-brand-text hover:bg-brand-surface dark:hover:bg-brand-surface'
                      }`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Search Input */}
            {showSearch && (
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-brand-text" />
                <Input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Поиск по названию или артикулу..."
                  className="w-full pl-12 pr-10 py-3 rounded-xl bg-brand-surface dark:bg-brand-surface border-0 dark:text-brand-text"
                  autoFocus
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-brand-text hover:text-brand-text"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>
            )}

            {/* Collections */}
            {collections.length > 0 && (
              <div className="flex flex-wrap gap-2 items-center">
                <span className="text-sm text-brand-text dark:text-brand-text mr-2">Коллекция:</span>
                <button
                  onClick={() => setSelectedCollection('')}
                  className={`px-3 py-1.5 text-sm transition-all rounded-lg ${
                    !selectedCollection
                      ? 'bg-brand-accent dark:bg-brand-accent text-brand-text dark:text-brand-text'
                      : 'bg-brand-surface dark:bg-brand-surface text-brand-text dark:text-brand-text hover:bg-brand-surface dark:hover:bg-brand-surface'
                  }`}
                >
                  Все
                </button>
                {collections.map(col => (
                  <button
                    key={col}
                    onClick={() => setSelectedCollection(col)}
                    className={`px-3 py-1.5 text-sm transition-all rounded-lg ${
                      selectedCollection === col
                        ? 'bg-brand-accent dark:bg-brand-accent text-brand-text dark:text-brand-text'
                        : 'bg-brand-surface dark:bg-brand-surface text-brand-text dark:text-brand-text hover:bg-brand-surface dark:hover:bg-brand-surface'
                    }`}
                  >
                    {col}
                  </button>
                ))}
              </div>
            )}

            {/* Active Filters */}
            {(selectedCategory !== 'Все' || selectedCollection || searchQuery) && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-brand-text dark:text-brand-text">Активные фильтры:</span>
                {selectedCategory !== 'Все' && (
                  <span className="px-2 py-1 text-xs bg-brand-surface dark:bg-brand-surface text-brand-text dark:text-brand-text rounded-lg">
                    {selectedCategory}
                  </span>
                )}
                {selectedCollection && (
                  <span className="px-2 py-1 text-xs bg-brand-surface dark:bg-brand-surface text-brand-text dark:text-brand-text rounded-lg">
                    {selectedCollection}
                  </span>
                )}
                {searchQuery && (
                  <span className="px-2 py-1 text-xs bg-brand-surface dark:bg-brand-surface text-brand-text dark:text-brand-text rounded-lg">
                    "{searchQuery}"
                  </span>
                )}
                <button
                  onClick={clearFilters}
                  className="text-sm text-brand-text hover:text-brand-text ml-2"
                >
                  Сбросить
                </button>
              </div>
            )}
          </div>

          {/* Products Grid */}
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-brand-text" />
            </div>
          ) : products.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6 lg:gap-8">
              {products.map(product => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          ) : (
            <div data-nosnippet="" className="text-center py-16">
              <p className="text-brand-text dark:text-brand-text">Товары не найдены</p>
              <p className="text-sm text-brand-text dark:text-brand-text mt-2">
                {searchQuery ? 'Попробуйте изменить запрос' : 'Коллекция скоро появится'}
              </p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default HomePage;
